
import { defineComponent, inject } from 'vue';
import { useCloud_Action_AsyncMutation, useSettingsPageLinuxQuery, useSettingsPageWindowsQuery } from '@/graphql/codegen/generated/graphql';
import { longPolling } from '@/services/PlatfromGQL/longPolling';
import { Cloud_Action_Type } from '@/graphql/codegen/gql/graphql';
import { CLOUD_STATUS } from '@/views/settings/settingsCloud/cloud.const';
import { useNotify } from '@/services/notify/notify';
import IbOkCancelDialog from '@/components/common/dialogs/OkCancelDialog.vue';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { openCloudRedirectUrl } from '@/views/settings/settingsCloud/settingsCloud.utils';
import { useUserRolePermissions } from '@/services/UserRolePermissions/UserRolePermissions';
import { useMatomo } from '@/services/matomo/matomo.service';
import { MatomoCategories, MatomoSettingsActions } from '@/services/matomo/matomo.const';
import { useOsService } from '@/services/OsService/OsService';
import CloudDisabled from './cloudDisabled.vue';
import CloudEnabled from './cloudEnabled.vue';
import CloudPending from './cloudPending.vue';
import CloudError from './cloudError.vue';
import { ResponseStatus } from '../../../const/globals';

export default defineComponent({
  name: 'SettingsCloud',
  components: {
    CloudDisabled,
    CloudEnabled,
    CloudPending,
    CloudError,
    'ib-ok-cancel-dialog': IbOkCancelDialog as any,
  },

  setup() {
    const matomo = useMatomo();
    const userRolePermissions = useUserRolePermissions();
    const coordinatorsService = inject(IKCoordinatorsService);
    const osService = useOsService();
    const GQL_Cloud_Action_AsyncMutation = useCloud_Action_AsyncMutation({});
    const settingsCaller = osService?.isLinux.value ? useSettingsPageLinuxQuery : useSettingsPageWindowsQuery;
    const GQL_SettingsPageQuery = settingsCaller(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    const { toastMessage } = useNotify();
    return { matomo, userRolePermissions, coordinatorsService, GQL_SettingsPageQuery, GQL_Cloud_Action_AsyncMutation, toastMessage };
  },
  data() {
    return {
      isPauseCloudDialogOpen: false,
      isDeactivateCloudDialogOpen: false,
      CLOUD_STATUS,
    };
  },
  computed: {
    cloudStatus() {
      return CLOUD_STATUS[this.GQL_SettingsPageQuery?.result?.value?.ibManagerEntities?.settings?.common?.cloud?.cloudStatus as keyof typeof CLOUD_STATUS] || CLOUD_STATUS.Disabled;
    },
  },
  methods: {
    async scaleToCloud() {
      this.matomo?.trackEvent({
        category: MatomoCategories.Settings,
        action: MatomoSettingsActions.ScaleToCloud,
      });
      await openCloudRedirectUrl(this.coordinatorsService?.selectedCoordinator.value || '', this.cloudStatus);
    },
    deactivateCloud() {
      this.isDeactivateCloudDialogOpen = true;
    },
    async deactivateCloudConfirmed() {
      this.isDeactivateCloudDialogOpen = false;
      const deactivateCloudId = await this.GQL_Cloud_Action_AsyncMutation.mutate({
        data: {
          coordinatorId: this.coordinatorsService?.selectedCoordinator.value || '',
          action: Cloud_Action_Type.Deactivate,
        },
      });
      const resultData = await longPolling.fetch('cloud_action_asyncResult', deactivateCloudId?.data?.cloud_action_async);
      if (resultData?.data?.cloud_action_asyncResult?.result?.status !== ResponseStatus.Ok) {
        this.toastMessage(this.$t('settings.cloud.dialogs.deactivate.error'), 'strong');
      }
    },
    pauseCloud() {
      this.isPauseCloudDialogOpen = true;
    },
    async pauseCloudConfirmed(event: any) {
      this.isPauseCloudDialogOpen = false;
      const pauseCloudId = await this.GQL_Cloud_Action_AsyncMutation.mutate({
        data: {
          coordinatorId: this.coordinatorsService?.selectedCoordinator.value || '',
          action: event.checkBoxValue ? Cloud_Action_Type.PauseAndDelete : Cloud_Action_Type.Pause,
        },
      });
      const resultData = await longPolling.fetch('cloud_action_asyncResult', pauseCloudId?.data?.cloud_action_async);
      if (resultData?.data?.cloud_action_asyncResult?.result?.status === ResponseStatus.Ok) {
        this.setCloudPausedDate();
      } else {
        this.toastMessage(this.$t('settings.cloud.dialogs.pause.error'), 'strong');
      }
    },
    async resumeCloud() {
      const resumeCloudId = await this.GQL_Cloud_Action_AsyncMutation.mutate({
        data: {
          coordinatorId: this.coordinatorsService?.selectedCoordinator.value || '',
          action: Cloud_Action_Type.Resume,
        },
      });
      const resultData = await longPolling.fetch('cloud_action_asyncResult', resumeCloudId?.data?.cloud_action_async);
      if (resultData?.data?.cloud_action_asyncResult?.result?.status === ResponseStatus.Ok) {
        this.removeCloudPauseDate();
      } else {
        this.toastMessage(this.$t('settings.cloud.dialogs.resume.error'), 'strong');
      }
    },
    dialogCanceled() {
      this.isPauseCloudDialogOpen = false;
      this.isDeactivateCloudDialogOpen = false;
    },
    connectWithSales() {
      const salesMailUrl = process.env.VUE_APP_CLOUD_SALES_MAIL;
      const win = window.open(salesMailUrl, '_blank');
      win?.focus();
    },
    setCloudPausedDate() {
      localStorage.setItem('CloudPausedDate', Date.now().toString());
    },
    removeCloudPauseDate() {
      localStorage.removeItem('CloudPausedDate');
    },
  },
});
